import React, { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import {
  BuildingIcon,
  CircleIcon,
  LinkIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UnlinkIcon,
  UserCheckIcon,
} from 'lucide-react'
import { OUTREACH_TARGET_STATUSES, OUTREACH_TARGET_STATUS_LABELS, SOURCE_CHANNEL_META, type OutreachTargetStatus } from '@repo/types'
import { Button, EMPTY_VALUE } from '@repo/ui'

import { ActionDropdown, ConfirmSettingModal, EmptyState, Header, StatusChip, Table, TwoLineCell } from 'components'
import type { ActionDropdownItem } from 'components/input/ActionDropdown/ActionDropdown'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { OUTREACH_CAMPAIGN_TYPE_CHIPS, OUTREACH_TARGET_STATUS_CHIPS } from 'constants/statuses'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useOutreachActions, useOutreachCampaign } from 'hooks/api/useGtmOutreach'
import { ROUTES } from 'routes'
import type { OutreachTarget } from 'services'
import { date, getErrorMessage } from 'utils'

import { CampaignModal, LinkDemoModal, TargetModal } from './outreachModals'
import { formatCampaignDates, formatMeetingDay, formatTripCost, statusInSentence } from './outreachModel'

/** VRG-696 — the channel outreach records, by its one label (#13 Sales; the stored value is `outreach`). */
const OUTREACH_CHANNEL_LABEL = SOURCE_CHANNEL_META.outreach.label

const ADMIN_TIP = 'Only a Vera admin can change outreach campaigns.'
const cellSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

const Dash = () => (
  <Box sx={cellSx}>
    <Typography variant="body2" color="text.disabled">
      {EMPTY_VALUE}
    </Typography>
  </Box>
)

type Dialog =
  | { kind: 'edit-campaign' | 'delete-campaign' | 'add-target' }
  | { kind: 'edit-target' | 'delete-target' | 'link-demo'; target: OutreachTarget }
  | null

/**
 * `/internal/gtm/sales/campaigns/:id` (VRG-713; was `/internal/gtm/sales/outreach/:id`, VRG-684,
 * and `/internal/gtm/outreach/:id`, VRG-679, both of which redirect here): one campaign and the brands it targets. Statuses move to contact → sent → replied → meeting booked →
 * meeting held → pilot → LOI signed (or declined). A reply or a meeting is linked to
 * People (the contact's email, tagged Outreach) or to a demo (its company, tagged Outreach), so
 * GTM → Sales counts what this campaign produced.
 */
export const GtmOutreachCampaignPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { open: notify } = useSnackbar()
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const { data, isLoading, error } = useOutreachCampaign(id)
  const actions = useOutreachActions()
  const [dialog, setDialog] = useState<Dialog>(null)
  const close = () => setDialog(null)
  const fail = (e: Error) => notify({ type: 'error', title: getErrorMessage(e) })
  const done = (title: string) => ({ onSuccess: () => { close(); notify({ type: 'success', title }) }, onError: fail })
  const tip = isAdmin ? undefined : ADMIN_TIP

  const campaign = data?.campaign
  const targets = data?.targets ?? []

  const menu = (t: OutreachTarget): ActionDropdownItem[] => [
    { label: 'Edit brand', icon: <PencilIcon strokeWidth={1} />, disabled: !isAdmin, tooltip: tip, onClick: () => setDialog({ kind: 'edit-target', target: t }) },
    // Quick status moves: every status but the current one.
    ...OUTREACH_TARGET_STATUSES.filter((s) => s !== t.status).map((s, i) => ({
      label: `Mark ${statusInSentence(s)}`,
      icon: <CircleIcon strokeWidth={1} color={OUTREACH_TARGET_STATUS_CHIPS[s].color} />,
      divider: i === 0,
      disabled: !isAdmin,
      tooltip: tip,
      onClick: () =>
        actions.updateTarget.mutate(
          { id: t.id, input: { status: s as OutreachTargetStatus } },
          { onSuccess: () => notify({ type: 'success', title: `${t.brand_name}: ${OUTREACH_TARGET_STATUS_LABELS[s]}` }), onError: fail },
        ),
    })),
    t.person_key
      ? { label: 'Unlink from Access', icon: <UnlinkIcon strokeWidth={1} />, divider: true, disabled: !isAdmin, tooltip: tip, onClick: () => actions.unlinkPerson.mutate(t.id, { onError: fail }) }
      : {
          label: 'Link to Access',
          icon: <UserCheckIcon strokeWidth={1} />,
          divider: true,
          disabled: !isAdmin || !t.contact_email,
          tooltip: !isAdmin ? ADMIN_TIP : !t.contact_email ? 'Add the contact email first' : undefined,
          onClick: () =>
            actions.linkPerson.mutate(t.id, {
              onSuccess: () => notify({ type: 'success', title: `${t.contact_email} recorded as ${OUTREACH_CHANNEL_LABEL} in Access` }),
              onError: fail,
            }),
        },
    t.demo_company
      ? { label: 'Unlink demo', icon: <UnlinkIcon strokeWidth={1} />, disabled: !isAdmin, tooltip: tip, onClick: () => actions.unlinkDemo.mutate(t.id, { onError: fail }) }
      : { label: 'Link a demo', icon: <LinkIcon strokeWidth={1} />, disabled: !isAdmin, tooltip: tip, onClick: () => setDialog({ kind: 'link-demo', target: t }) },
    { label: 'Remove brand', icon: <Trash2Icon strokeWidth={1} />, destructive: true, divider: true, disabled: !isAdmin, tooltip: tip, onClick: () => setDialog({ kind: 'delete-target', target: t }) },
  ]

  const columns = useMemo(
    () => [
      {
        field: 'brand_name',
        headerName: 'Brand',
        type: 'string',
        width: 220,
        minWidth: 180,
        flex: 220,
        pinned: 'left',
        disableReorder: true,
        valueGetter: (_v: unknown, r: OutreachTarget) => `${r.brand_name} ${r.website ?? ''}`,
        renderCell: ({ row }: { row: OutreachTarget }) => <TwoLineCell top={row.brand_name} bottom={row.website} title={row.notes ?? undefined} />,
      },
      {
        field: 'contact',
        headerName: 'Contact',
        type: 'string',
        width: 220,
        minWidth: 180,
        flex: 220,
        valueGetter: (_v: unknown, r: OutreachTarget) => `${r.contact_name ?? ''} ${r.contact_email ?? ''}`,
        renderCell: ({ row }: { row: OutreachTarget }) =>
          row.contact_name || row.contact_email ? <TwoLineCell top={row.contact_name ?? row.contact_email} bottom={row.contact_name ? row.contact_email : null} /> : <Dash />,
      },
      {
        field: 'industry',
        headerName: 'Industry',
        type: 'multiSelect',
        width: 170,
        valueGetter: (_v: unknown, r: OutreachTarget) => r.industry ?? 'Not set',
        renderCell: ({ row, filter, value }: { row: OutreachTarget; filter?: boolean; value?: string }) =>
          filter ? value : row.industry ? <Box sx={cellSx}><Typography variant="body2" noWrap>{row.industry}</Typography></Box> : <Dash />,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'statusChip',
        options: statusFilterOptions(OUTREACH_TARGET_STATUS_CHIPS),
        width: 170,
        renderCell: ({ row }: { row: OutreachTarget }) => (
          <Stack justifyContent="center" spacing={0.5} sx={{ height: '100%' }}>
            <Box>
              <StatusChip size="small" statuses={OUTREACH_TARGET_STATUS_CHIPS} status={row.status} />
            </Box>
            <Typography variant="body3" color="text.secondary" noWrap>
              since {date(row.status_changed_at, 'MMM dd')}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'meeting_at',
        headerName: 'Meeting',
        type: 'dateTime',
        width: 170,
        valueGetter: (_v: unknown, r: OutreachTarget) => (r.meeting_at ? new Date(r.meeting_at) : null),
        renderCell: ({ row }: { row: OutreachTarget }) =>
          row.meeting_at ? <TwoLineCell top={formatMeetingDay(row.meeting_at)} bottom={row.meeting_notes} title={row.meeting_notes ?? undefined} /> : <Dash />,
      },
      {
        field: 'person_key',
        headerName: 'Access',
        type: 'string',
        width: 150,
        valueGetter: (_v: unknown, r: OutreachTarget) => (r.person_key ? 'Linked' : ''),
        renderCell: ({ row }: { row: OutreachTarget }) =>
          row.person_key ? (
            <Box sx={cellSx}>
              <Typography component={RouterLink} to={ROUTES.INTERNAL_ACCESS} variant="body2" title={`${row.person_key} · source channel ${OUTREACH_CHANNEL_LABEL}`} sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                Outreach
              </Typography>
            </Box>
          ) : (
            <Dash />
          ),
      },
      {
        field: 'demo_company',
        headerName: 'Demo',
        type: 'string',
        width: 180,
        valueGetter: (_v: unknown, r: OutreachTarget) => r.demo_company?.name ?? '',
        renderCell: ({ row }: { row: OutreachTarget }) =>
          row.demo_company ? (
            <Box sx={cellSx}>
              <Typography component={RouterLink} to={ROUTES.INTERNAL_COMPANY(row.demo_company.id)} variant="body2" noWrap sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                {row.demo_company.name}
              </Typography>
            </Box>
          ) : (
            <Dash />
          ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: OutreachTarget }) => <ActionDropdown items={menu(row)} />,
      },
    ],
    // `menu` closes over setters and the admin flag only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targets, isAdmin],
  )

  const status = (error as (Error & { status?: number }) | null)?.status
  const subtitle = campaign
    ? [
        campaign.city,
        formatCampaignDates(campaign.starts_on, campaign.ends_on),
        `${campaign.total} brands`,
        campaign.trip_cost ? `trip cost ${formatTripCost(campaign.trip_cost)}` : null,
      ]
        .filter((x) => x && x !== EMPTY_VALUE)
        .join(' · ')
    : undefined

  const adminButton = (label: string, onClick: () => void, opts: { primary?: boolean; destructive?: boolean; icon?: React.ReactNode } = {}) => (
    <Tooltip title={isAdmin ? '' : ADMIN_TIP}>
      <span style={{ display: 'inline-flex' }}>
        <Button
          variant={opts.primary ? 'primary' : 'secondary'}
          destructive={opts.destructive ? 'hover' : undefined}
          disableElevation
          disabled={!isAdmin || !campaign}
          onClick={onClick}
          startIcon={opts.icon}
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  )

  return (
    <>
      <Header
        title={campaign?.name ?? (isLoading ? '' : 'Campaign')}
        breadcrumbsOveride={campaign?.name ?? 'Campaign'}
        titleAdornment={campaign ? <Box sx={{ ml: 2 }}><StatusChip size="small" statuses={OUTREACH_CAMPAIGN_TYPE_CHIPS} status={campaign.type} /></Box> : undefined}
        description={campaign ? [subtitle, campaign.notes].filter(Boolean).join('. ') : undefined}
        rightComponents={
          <>
            {adminButton('Edit', () => setDialog({ kind: 'edit-campaign' }), { icon: <PencilIcon strokeWidth={1} /> })}
            {adminButton('Delete', () => setDialog({ kind: 'delete-campaign' }), { destructive: true, icon: <Trash2Icon strokeWidth={1} /> })}
            {adminButton('Add brand', () => setDialog({ kind: 'add-target' }), { primary: true, icon: <PlusIcon strokeWidth={1} /> })}
          </>
        }
      />
      <Table
        loading={isLoading}
        columns={columns}
        rows={targets}
        alwaysShowRowSelection={false}
        fillBottom
        paginationMode="client"
        initialPageSize={100}
        viewKey="internal-gtm-outreach-campaign"
        emptyState={
          <EmptyState
            title={status === 404 ? 'Campaign not found' : status === 403 ? 'You cannot view campaigns' : error ? 'Could not load the campaign' : 'No brands yet'}
            text={
              status === 404
                ? 'It may have been deleted. Go back to Campaigns.'
                : error
                  ? 'The request failed. Reload the page, or check the API is running.'
                  : 'Add the brands this campaign targets. The Playbook tab on Sales has the qualifying rule.'
            }
            icon={<BuildingIcon strokeWidth={1} size={32} />}
          />
        }
      />

      {campaign && (
        <>
          <CampaignModal
            open={dialog?.kind === 'edit-campaign'}
            campaign={campaign}
            loading={actions.updateCampaign.isPending}
            onClose={close}
            onSave={(input) => actions.updateCampaign.mutate({ id: campaign.id, input }, done('Campaign saved'))}
          />
          <ConfirmSettingModal
            open={dialog?.kind === 'delete-campaign'}
            onClose={close}
            destructive
            loading={actions.deleteCampaign.isPending}
            title="Delete campaign?"
            confirmLabel="Delete"
            consequence={<><strong>{campaign.name}</strong> and its {campaign.total} brands are deleted. Source channels already recorded on People and Companies stay.</>}
            onConfirm={() =>
              actions.deleteCampaign.mutate(campaign.id, {
                onSuccess: () => { close(); notify({ type: 'success', title: 'Campaign deleted' }); navigate(`${ROUTES.INTERNAL_GTM_SALES}?tab=campaigns`) },
                onError: fail,
              })
            }
          />
          <TargetModal
            open={dialog?.kind === 'add-target' || dialog?.kind === 'edit-target'}
            target={dialog?.kind === 'edit-target' ? dialog.target : null}
            loading={actions.addTarget.isPending || actions.updateTarget.isPending}
            onClose={close}
            onSave={(input) =>
              dialog?.kind === 'edit-target'
                ? actions.updateTarget.mutate({ id: dialog.target.id, input }, done('Brand saved'))
                : actions.addTarget.mutate({ campaignId: campaign.id, input }, done('Brand added'))
            }
          />
          <LinkDemoModal
            open={dialog?.kind === 'link-demo'}
            brand={dialog?.kind === 'link-demo' ? dialog.target.brand_name : ''}
            loading={actions.linkDemo.isPending}
            onClose={close}
            onLink={(companyId) => dialog?.kind === 'link-demo' && actions.linkDemo.mutate({ id: dialog.target.id, companyId }, done(`Demo linked, recorded as ${OUTREACH_CHANNEL_LABEL}`))}
          />
          <ConfirmSettingModal
            open={dialog?.kind === 'delete-target'}
            onClose={close}
            destructive
            loading={actions.deleteTarget.isPending}
            title="Remove brand?"
            confirmLabel="Remove"
            consequence={<><strong>{dialog?.kind === 'delete-target' ? dialog.target.brand_name : ''}</strong> is removed from this campaign with its status and notes. A source channel it recorded stays.</>}
            onConfirm={() => dialog?.kind === 'delete-target' && actions.deleteTarget.mutate(dialog.target.id, done('Brand removed'))}
          />
        </>
      )}
    </>
  )
}
